import { useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import type { VideoPlayerRef } from '../video-player/VideoPlayer.data';
import useFullScreenChange from '../../../../hooks/useFullScreenChange';

export const useFullScreen = (
  playerRef: RefObject<VideoPlayerRef | null>,
): {
  enterFullScreen: () => void;
  exitFullScreen: () => void;
  toggleFullScreen: () => void;
  isFullscreen: boolean;
} => {
  const isFullscreen = useFullScreenChange();
  const enterFullScreen = useCallback(() => {
    const internalPlayer = playerRef.current?.getInternalPlayer();
    if (internalPlayer) {
      const { element } = internalPlayer;
      if (element && 'requestFullscreen' in element) {
        element.requestFullscreen();
      } else if (element && 'webkitEnterFullScreen' in element) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (element as any).webkitEnterFullScreen();
      } else if (element && 'webkitRequestFullScreen' in element) {
        // Case for Safari desktop
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (element as any).webkitRequestFullScreen();
      } else if (element && 'webkitEnterFullscreen' in element) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (element as any).webkitEnterFullscreen();
      }
    }
  }, [playerRef]);

  const body = useRef(typeof window !== 'undefined' ? document : null);

  const exitFullScreen = useCallback(() => {
    const internalPlayer = playerRef.current?.getInternalPlayer();
    if (internalPlayer) {
      const { element } = internalPlayer;

      if (element && 'exitFullscreen' in element) {
        element.exitFullscreen();
      } else if (element && 'webkitExitFullScreen' in element) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (element as any).webkitExitFullScreen();
      }
    }
  }, [playerRef]);

  return {
    enterFullScreen,
    exitFullScreen,
    isFullscreen,
    toggleFullScreen: async () => {
      if (!!body.current && body.current.fullscreenElement) {
        return exitFullScreen();
      }

      return enterFullScreen();
    },
  };
};
